import React, { useEffect, useState } from 'react'
import { adminService } from '../../services/admin'
import { useAccessContext } from '../../context/AccessContext'
import { PricingWorkspace } from '../billing/PricingWorkspace'
import { PlatformBillingFeatureControl } from '../billing/PlatformBillingFeatureControl'

const money = (cents?: number) => `$${((cents || 0) / 100).toFixed(2)}`
const label = (value?: string) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
const dateLabel = (value?: string | null) => value ? new Date(value).toLocaleDateString() : 'Not scheduled'
const recurringProductPrice = (product: any) => {
  if (product?.pricingModel === 'one_time') return `${money(product.oneTimePriceCents)} one-time`
  if (['contract', 'manual'].includes(product?.pricingModel)) return 'Contact sales'
  if (product?.pricingModel === 'usage_pack') return `${money(product.monthlyPriceCents || product.pricingRule?.baseAmountCents || 0)}/month package`
  return `${money(product?.monthlyPriceCents || 0)}/month`
}
const accelerationDimensionLabels: Record<string, string> = {
  'marketplace.posting.priority': 'Marketplace posting',
  'marketplace.response.batch_size': 'Candidate processing batch',
  'matching.refresh_interval': 'Matching refresh',
  'matching.autofill_interval': 'Auto-fill cadence',
  'matching.automation_level': 'Matching automation',
  'reports.queue_priority': 'Report queue',
  'reports.max_concurrency': 'Concurrent report jobs',
  'analytics.refresh_interval': 'Analytics refresh',
  'integrations.sync_interval': 'Integration synchronization',
}

const accelerationValue = (dimension: any) => {
  if (!dimension) return 'Standard'
  if (dimension.unit === 'seconds' && typeof dimension.value === 'number') {
    if (dimension.value === 0) return 'Immediate when available'
    if (dimension.value % 3600 === 0) return `Every ${dimension.value / 3600} hour${dimension.value === 3600 ? '' : 's'}`
    if (dimension.value % 60 === 0) return `Every ${dimension.value / 60} minutes`
  }
  return label(String(dimension.value))
}

const FreeAccelerationBillingView: React.FC<{
  plans: any[]
  current: any
  billingState: any
  billingCalculation: any
  usage: any
  selectedPlanKey: string
  loading: boolean
  portalState: any
  paymentMethod: any
  autofill: any
  autofillReason: string
  onSelectedPlanKeyChange: (planKey: string) => void
  onAutofillReasonChange: (reason: string) => void
  onToggleAutofillExecution: () => void
  onSelectPlan: () => void
  onOpenPortal: () => void
}> = ({ plans, current, billingState, billingCalculation, usage, selectedPlanKey, loading, portalState, paymentMethod, autofill, autofillReason, onSelectedPlanKeyChange, onAutofillReasonChange, onToggleAutofillExecution, onSelectPlan, onOpenPortal }) => {
  const profile = current?.commercialProfile || {}
  const dimensions = profile.dimensions || {}
  const planKey = billingState.planKey || current?.subscription?.planKey || profile.planKey || 'free'
  const planName = billingState.planName || current?.plan?.name || (planKey === 'free' ? 'Free' : label(planKey))
  const activeWorkers = Number(billingCalculation.activeWorkers ?? current?.usage?.usage?.active_workers ?? usage?.usage?.active_workers ?? 0)
  const currentLocations = Number(billingCalculation.currentLocations ?? current?.usage?.usage?.locations ?? usage?.usage?.locations ?? 0)
  const accelerationEnabled = Boolean(profile.accelerationEnabled || planKey === 'acceleration')
  const selectableFree = plans.find((plan) => plan.planKey === 'free')
  const accelerationPlan = plans.find((plan) => plan.planKey === 'acceleration')
  const enterprisePlan = plans.find((plan) => plan.planKey === 'enterprise' || plan.planKey === 'self_hosted')
  const selectedCanSave = selectedPlanKey === 'free' && selectedPlanKey !== planKey && Boolean(selectableFree)
  const autofillExecutionEnabled = Boolean(autofill?.settings?.execution_enabled)
  const autofillExecuting = autofill?.commercialProfile?.effectiveAction === 'execute'
  const latestAutofillRun = autofill?.runs?.[0]
  return (
    <div className="billing-growth-shell free-acceleration-billing">
      <header className="billing-growth-header">
        <div><p className="eyebrow">Billing &amp; Plans</p><h1 className="heading-lg">Run your workforce for free</h1><p className="helper">Core workforce operations have no per-worker or per-location pricing. Upgrade for acceleration, automation, integrations, and advanced extras.</p></div>
        <a href="mailto:sales@nexusworkforce360.com?subject=Acceleration%20options" className="billing-support-link">Talk to Sales</a>
      </header>

      <section className="billing-current-strip panel" aria-label="Current commercial configuration">
        <div><span className="helper">Current plan</span><strong>{planName}</strong><span className="badge">{accelerationEnabled ? 'Acceleration active' : 'Standard priority'}</span></div>
        <div><span className="helper">Core platform price</span><strong>{planKey === 'free' ? '$0' : money(billingCalculation.normalizedMonthlyBaseCents)}</strong><span className="helper">No workforce-capacity charge</span></div>
        <div><span className="helper">Workers</span><strong>{activeWorkers.toLocaleString()}</strong><span className="helper">No per-worker pricing</span></div>
        <div><span className="helper">Locations</span><strong>{currentLocations.toLocaleString()}</strong><span className="helper">No per-location pricing</span></div>
        <div className="billing-strip-actions"><button type="button" className="btn btn-secondary" disabled={loading || !portalState.eligible} onClick={onOpenPortal}>Manage payment</button><a className="btn btn-secondary" href="#billing-invoices">Invoices</a></div>
      </section>

      <section className="panel stack" aria-labelledby="processing-profile-title">
        <div className="billing-section-heading"><div><h2 id="processing-profile-title">Current processing profile</h2><p className="helper">These values come from the backend entitlement service. Operational eligibility and authorization always remain authoritative.</p></div><span className="badge">{accelerationEnabled ? 'Accelerated' : 'Free · standard priority'}</span></div>
        <div className="setup-summary-grid">
          {Object.entries(accelerationDimensionLabels).map(([key, text]) => <div key={key}><span className="helper">{text}</span><strong>{accelerationValue(dimensions[key])}</strong></div>)}
        </div>
        {autofill ? <div className="stack">
          <div className="billing-section-heading"><div><h3>Auto-fill execution</h3><p className="helper">Cadence-driven recommendations always use current skills, verified documents, availability, conflicts, geography, relationship visibility, and shift capacity. Automatic assignment requires Acceleration and explicit Tenant Admin consent; every execution is revalidated by the canonical approval transaction.</p></div><span className="badge">{autofillExecuting ? 'Automatic execution enabled' : autofillExecutionEnabled ? 'Execution consent stored · inactive' : 'Recommendations only'}</span></div>
          <div className="setup-summary-grid"><div><span className="helper">Effective action</span><strong>{label(autofill?.commercialProfile?.effectiveAction || 'recommend')}</strong></div><div><span className="helper">Last evaluation</span><strong>{dateLabel(autofill?.state?.last_completed_at)}</strong></div><div><span className="helper">Next evaluation</span><strong>{dateLabel(autofill?.state?.next_due_at)}</strong></div><div><span className="helper">Latest candidates</span><strong>{Number(latestAutofillRun?.candidate_count || 0).toLocaleString()}</strong></div></div>
          <label className="stack"><span className="helper">Audit reason</span><textarea className="input" value={autofillReason} onChange={(event) => onAutofillReasonChange(event.target.value)} placeholder="Why is automatic assignment execution changing?" /></label>
          <button type="button" className={autofillExecutionEnabled ? 'btn btn-secondary' : 'btn btn-primary'} disabled={loading || autofillReason.trim().length < 3 || (!autofillExecutionEnabled && planKey !== 'acceleration')} onClick={onToggleAutofillExecution}>{autofillExecutionEnabled ? 'Disable automatic assignment' : planKey === 'acceleration' ? 'Enable automatic assignment' : 'Acceleration required for execution'}</button>
        </div> : null}
      </section>

      <section id="billing-plan-progression" className="billing-progression panel" aria-labelledby="commercial-options-title">
        <div className="billing-section-heading"><div><h2 id="commercial-options-title">Commercial options</h2><p className="helper">Free remains fully operational. Paid options change speed, priority, automation depth, integrations, and service levels.</p></div></div>
        <div className="billing-plan-path">
          <article className={`billing-plan-step ${planKey === 'free' ? 'active' : ''}`}><span className="billing-plan-ribbon">{planKey === 'free' ? 'Current plan' : 'Always available'}</span><h3>Free</h3><strong>$0 base</strong><span className="helper">Core Workforce Network OS</span><span className="billing-plan-proof">Standard processing priority</span><span className="billing-plan-proof">No per-worker pricing</span><span className="billing-plan-proof">No per-location pricing</span><button type="button" className="btn btn-secondary" disabled={loading || planKey === 'free'} onClick={() => onSelectedPlanKeyChange('free')}>{selectedPlanKey === 'free' && planKey !== 'free' ? 'Selected' : planKey === 'free' ? 'Current' : 'Move to Free'}</button></article>
          <article className={`billing-plan-step ${accelerationEnabled ? 'active' : ''}`}><h3>{accelerationPlan?.displayName || 'Acceleration'}</h3><strong>Configurable pricing</strong><span className="helper">Priority processing and faster cadence</span><span className="billing-plan-proof">Higher concurrency</span><span className="billing-plan-proof">Faster matching and auto-fill</span><span className="billing-plan-proof">Deeper automation</span><a className="btn btn-primary" href="mailto:sales@nexusworkforce360.com?subject=Configure%20Acceleration">{accelerationEnabled ? 'Review acceleration' : 'Explore acceleration'}</a></article>
          <article className={`billing-plan-step ${['enterprise','self_hosted'].includes(planKey) ? 'active' : ''}`}><h3>{enterprisePlan?.displayName || 'Enterprise / Self-hosted'}</h3><strong>Contact Sales</strong><span className="helper">Advanced security and deployment</span><span className="billing-plan-proof">SSO, MFA policies, and SLA</span><span className="billing-plan-proof">Data residency and procurement</span><span className="billing-plan-proof">Dedicated support</span><a className="btn btn-secondary" href="mailto:sales@nexusworkforce360.com?subject=Enterprise%20or%20self-hosted">Contact Sales</a></article>
        </div>
        {selectedCanSave ? <div className="billing-plan-actions"><button type="button" className="btn btn-primary" disabled={loading} onClick={onSelectPlan}>Confirm Free plan</button></div> : null}
      </section>

      <section id="billing-payment-settings" className="billing-payment-card panel"><h2>Payment method</h2><div className="setup-summary-grid"><div><span className="helper">Provider</span><strong>{label(current?.account?.providerKey || 'manual')}</strong></div><div><span className="helper">Payment profile</span><strong>{portalState.eligible ? 'Connected' : 'Not required for Free'}</strong></div><div><span className="helper">Card</span><strong>{paymentMethod.brand || paymentMethod.last4 ? `${label(paymentMethod.brand || 'Card')} ending ${paymentMethod.last4 || '----'}` : 'No payment method stored'}</strong></div></div></section>
    </div>
  )
}

const BillingInvoicesPanel: React.FC<{ invoicePreview: any; invoices: any[] }> = ({ invoicePreview, invoices }) => (
  <article id="billing-invoice-preview" className="panel workspace-panel stack">
    <div className="billing-section-heading">
      <h3>Invoice preview</h3>
      <strong>{money(invoicePreview?.totalCents)}</strong>
    </div>
    {(invoicePreview?.lines || []).map((line: any, index: number) => (
      <div key={`${line.description}-${index}`} className="inline-group" style={{ justifyContent: 'space-between' }}>
        <span>{line.description}</span>
        <strong>{money(line.amountCents)}</strong>
      </div>
    ))}
    {!(invoicePreview?.lines || []).length ? <p className="helper">No invoice preview is available yet.</p> : null}
    <div id="billing-invoices" className="billing-section-heading">
      <h3>Invoice History</h3>
      <a className="btn btn-secondary" href="#billing-invoices">View All Invoices</a>
    </div>
    <div className="table-scroll">
      <table className="admin-table">
        <thead><tr><th>Invoice</th><th>Date</th><th>Plan</th><th>Amount</th><th>Status</th><th>Download</th></tr></thead>
        <tbody>
          {invoices.map((invoice) => (
            <tr key={invoice.id}>
              <td>{invoice.invoiceNumber || invoice.id}</td>
              <td>{invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString() : 'n/a'}</td>
              <td>{invoice.planName || invoice.planKey || 'Current plan'}</td>
              <td>{money(invoice.totalCents)}</td>
              <td><span className="badge">{label(invoice.status)}</span></td>
              <td className="inline-group">
                {invoice.links?.viewUrl || invoice.hostedInvoiceUrl ? <a className="btn btn-secondary" href={invoice.links?.viewUrl || invoice.hostedInvoiceUrl} target="_blank" rel="noreferrer">View</a> : null}
                {invoice.links?.pdfUrl || invoice.invoicePdfUrl ? <a className="btn btn-secondary" href={invoice.links?.pdfUrl || invoice.invoicePdfUrl} target="_blank" rel="noreferrer">PDF</a> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {!invoices.length ? <p className="helper">No invoices recorded yet.</p> : null}
  </article>
)

const BillingCommercialStackPanel: React.FC<{ current: any; billingCalculation: any }> = ({ current, billingCalculation }) => {
  const entitlements = current?.entitlements || {}
  const includedAddons = entitlements.includedAddons || current?.includedAddons || []
  const paidAddons = entitlements.paidAddons || current?.paidAddons || []
  const pendingAddons = entitlements.pendingAddonRequests || current?.pendingAddonRequests || []
  const includedProducts = entitlements.includedProducts || current?.includedProducts || []
  const paidProducts = entitlements.paidProducts || current?.paidProducts || []
  const pendingProducts = entitlements.pendingProductRequests || current?.pendingProductRequests || []
  const enabledFeatures = entitlements.enabledFeatures || current?.enabledFeatures || []
  const visibleProducts = [...includedProducts, ...paidProducts, ...pendingProducts]
  const hasStack = includedAddons.length || paidAddons.length || pendingAddons.length || visibleProducts.length
  return (
    <article className="panel workspace-panel stack">
      <div className="billing-section-heading">
        <div>
          <h3>Commercial stack</h3>
          <p className="helper">Included modules, paid products, and assisted commercial requests attached to this tenant.</p>
        </div>
        <strong>{money((billingCalculation.addonsTotalCents || 0) + (billingCalculation.productsTotalCents || 0))} / month</strong>
      </div>
      <div className="setup-summary-grid">
        <div><span className="helper">Included add-ons</span><strong>{includedAddons.length}</strong></div>
        <div><span className="helper">Paid add-ons</span><strong>{money(billingCalculation.addonsTotalCents)}</strong></div>
        <div><span className="helper">Paid products</span><strong>{money(billingCalculation.productsTotalCents)}</strong></div>
        <div><span className="helper">Pending requests</span><strong>{pendingAddons.length + pendingProducts.length}</strong></div>
        <div><span className="helper">Enabled features</span><strong>{enabledFeatures.length}</strong></div>
        <div><span className="helper">Pricing source</span><strong>{paidProducts.some((product: any) => product.pricingSource === 'tenant_pricing_override') ? 'Negotiated' : paidProducts.length ? 'Catalog' : 'Plan'}</strong></div>
      </div>
      {hasStack ? (
        <div className="billing-grid">
          <div className="stack">
            <strong>Included add-ons</strong>
            {includedAddons.map((addon: any) => (
              <div key={addon.addonKey} className="inline-group" style={{ justifyContent: 'space-between' }}>
                <span>{addon.name}</span><span className="badge">Included</span>
              </div>
            ))}
            {!includedAddons.length ? <p className="helper">No plan-included add-ons for this plan.</p> : null}
          </div>
          <div className="stack">
            <strong>Paid add-ons</strong>
            {paidAddons.map((addon: any) => (
              <div key={addon.addonKey} className="inline-group" style={{ justifyContent: 'space-between' }}>
                <span>{addon.name}<br /><span className="helper">{label(addon.source || addon.subscriptionStatus || 'active')}</span></span>
                <strong>{money(addon.monthlyPriceCents)} / month</strong>
              </div>
            ))}
            {pendingAddons.map((addon: any) => (
              <div key={addon.addonKey} className="inline-group" style={{ justifyContent: 'space-between' }}>
                <span>{addon.name}</span><span className="badge">{label(addon.subscriptionStatus || 'pending')}</span>
              </div>
            ))}
            {!paidAddons.length && !pendingAddons.length ? <p className="helper">No paid add-ons enabled.</p> : null}
          </div>
          <div className="stack">
            <strong>Products and packs</strong>
            {visibleProducts.map((product: any) => (
              <div key={`${product.productKey}-${product.subscriptionStatus || product.entitlementSource || 'included'}`} className="inline-group" style={{ justifyContent: 'space-between' }}>
                <span>{product.name}<br /><span className="helper">{product.productStatus || label(product.subscriptionStatus || product.entitlementSource || product.source)}</span></span>
                <strong>{product.included ? 'Included' : recurringProductPrice(product)}</strong>
              </div>
            ))}
            {!visibleProducts.length ? <p className="helper">No commercial products enabled.</p> : null}
          </div>
        </div>
      ) : <p className="helper">No add-ons or commercial products are attached yet.</p>}
    </article>
  )
}

export const Billing: React.FC<{ mode?: 'standard' | 'advanced-pricing' }> = ({ mode = 'standard' }) => {
  const { accessContext, hasAnyPermission } = useAccessContext()
  if (mode === 'advanced-pricing') return <PricingWorkspace allowed={Boolean(accessContext?.canViewPlatformBilling || accessContext?.canManagePlatformBilling || accessContext?.isSystemAdmin || accessContext?.isSystemSuperAdmin || accessContext?.uiCapabilities?.modules?.platformBilling || hasAnyPermission(['platform.billing.pricing.view', 'platform.billing.pricing.manage']))} />
  return <TenantBilling />
}
const TenantBilling: React.FC = () => {
  const [tenantBillingView, setTenantBillingView] = useState(false)
  const { accessContext, hasAnyPermission, refresh: refreshAccessContext } = useAccessContext()
  const canManageTenantBilling = Boolean(accessContext?.canManageTenantBilling)
    || Boolean(accessContext?.uiCapabilities?.modules?.billing)
    || hasAnyPermission(['tenant.billing.manage', 'billing.access'])
  const canManagePlatformBilling = Boolean(accessContext?.uiCapabilities?.modules?.platformBilling)
    || Boolean(accessContext?.canViewPlatformBilling)
    || Boolean(accessContext?.canManagePlatformBilling)
    || Boolean(accessContext?.isSystemAdmin)
    || Boolean(accessContext?.isSystemSuperAdmin)
  const canEditPlatformBilling = Boolean(accessContext?.canManagePlatformBilling)
    || Boolean(accessContext?.isSystemAdmin)
    || Boolean(accessContext?.isSystemSuperAdmin)
  const [plans, setPlans] = useState<any[]>([])
  const [current, setCurrent] = useState<any>(null)
  const [autofill, setAutofill] = useState<any>(null)
  const [autofillReason, setAutofillReason] = useState('')
  const [usage, setUsage] = useState<any>(null)
  const [invoicePreview, setInvoicePreview] = useState<any>(null)
  const [invoices, setInvoices] = useState<any[]>([])
  const [selectedPlanKey, setSelectedPlanKey] = useState('')
  const [billingFrequency, setBillingFrequency] = useState<'monthly' | 'annual' | 'manual'>('monthly')
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const loadTenantBilling = async () => {
    if (!canManageTenantBilling || (canManagePlatformBilling && !tenantBillingView)) return
    const [plansResponse, currentResponse, usageResponse, invoiceResponse, invoicesResponse] = await Promise.all([
      adminService.listBillingPlans(),
      adminService.getBillingCurrent(),
      adminService.getBillingUsage(),
      adminService.getBillingInvoicePreview(),
      adminService.getBillingInvoices(),
    ])
    const loadedPlans = plansResponse.data?.plans || []
    setPlans(loadedPlans)
    setCurrent(currentResponse.data || null)
    setUsage(usageResponse.data || null)
    setInvoicePreview(invoiceResponse.data?.invoice || null)
    setInvoices(invoicesResponse.data?.invoices || [])
    setSelectedPlanKey(currentResponse.data?.subscription?.planKey || loadedPlans[0]?.planKey || '')
    setBillingFrequency(currentResponse.data?.subscription?.billingFrequency || 'monthly')
    try {
      const autofillResponse = await adminService.getMatchingAutofill()
      setAutofill(autofillResponse.data || null)
    } catch {
      setAutofill(null)
    }
  }

  const refresh = async () => {
    setLoading(true); setError('')
    try { await loadTenantBilling() }
    catch (err: any) { setError(err?.response?.data?.message || 'Unable to load billing.') }
    finally { setLoading(false) }
  }
  useEffect(() => { void refresh() }, [canManageTenantBilling, canManagePlatformBilling, tenantBillingView])
  const billingState = current?.billingState || {}
  const portalState = current?.actions?.portal || {}
  const paymentMethod = current?.paymentMethod || {}
  const billingCalculation = current?.billingCalculation || invoicePreview?.calculation || {}
  const selectPlan = async () => {
    if (!selectedPlanKey) return
    setLoading(true)
    setStatus('')
    setError('')
    try {
      await adminService.selectBillingPlan({ planKey: selectedPlanKey, billingFrequency })
      setStatus('Plan selection saved.')
      await Promise.all([refresh(), refreshAccessContext()])
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to save plan selection.')
    } finally {
      setLoading(false)
    }
  }

  const openBillingPortal = async () => {
    setLoading(true)
    setStatus('')
    setError('')
    try {
      const response = await adminService.createBillingPortalSession({ returnUrl: `${window.location.origin}/billing` })
      const url = response.data?.session?.url
      if (url) window.location.assign(url)
      else setError('Stripe portal did not return a redirect URL.')
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to open Stripe billing portal.')
    } finally {
      setLoading(false)
    }
  }

  const toggleAutofillExecution = async () => {
    if (!autofill || autofillReason.trim().length < 3) return
    setLoading(true)
    setStatus('')
    setError('')
    try {
      await adminService.updateMatchingAutofillSettings({ executionEnabled: !Boolean(autofill.settings?.execution_enabled), reason: autofillReason.trim() })
      const response = await adminService.getMatchingAutofill()
      setAutofill(response.data || null)
      setAutofillReason('')
      setStatus(autofill.settings?.execution_enabled ? 'Automatic assignment disabled; recommendations remain active.' : 'Automatic assignment enabled with canonical eligibility revalidation.')
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.response?.data?.message || 'Unable to update auto-fill execution.')
    } finally {
      setLoading(false)
    }
  }

  return <section className="panel workspace-panel stack" data-testid="workspace-panel">
    {!canManageTenantBilling && !canManagePlatformBilling && <><h1 className="heading-lg">Billing &amp; Plan</h1><p className="helper">Billing access is required.</p></>}
    {error && <p className="error-text">{error}</p>}
    {status && <p className="success-text">{status}</p>}
      {canManagePlatformBilling && canManageTenantBilling ? <nav className="platform-billing-tabs" aria-label="Billing workspace"><button type="button" className={!tenantBillingView ? 'active' : ''} onClick={() => setTenantBillingView(false)}>Platform billing</button><button type="button" className={tenantBillingView ? 'active' : ''} onClick={() => setTenantBillingView(true)}>Current tenant plan</button></nav> : null}
      {canManagePlatformBilling && !tenantBillingView ? <PlatformBillingFeatureControl canManage={canEditPlatformBilling} canManagePolicy={Boolean(accessContext?.isSystemSuperAdmin)} initialTenantId={accessContext?.tenantId || ''} /> : null}
      {canManageTenantBilling && (!canManagePlatformBilling || tenantBillingView) ? <section className="admin-card stack" aria-label="Document Intelligence plan information"><h2>Document Intelligence</h2><p>Document processing is configured per tenant, separately from the subscription plan. Existing uploads remain available. A system super admin must enable processing policy after the feature access is granted.</p>{canManagePlatformBilling ? <button type="button" className="btn btn-primary" onClick={() => { window.location.assign('/billing?section=documents') }}>Configure Document Intelligence</button> : <p>Ask your platform billing administrator to review Document Intelligence, CV extraction and OCR access for this tenant.</p>}</section> : null}

      {canManageTenantBilling && (!canManagePlatformBilling || tenantBillingView) ? (
        <FreeAccelerationBillingView
          plans={plans}
          current={current}
          billingState={billingState}
          billingCalculation={billingCalculation}
          usage={usage}
          selectedPlanKey={selectedPlanKey}
          portalState={portalState}
          paymentMethod={paymentMethod}
          autofill={autofill}
          autofillReason={autofillReason}
          loading={loading}
          onSelectedPlanKeyChange={setSelectedPlanKey}
          onAutofillReasonChange={setAutofillReason}
          onToggleAutofillExecution={toggleAutofillExecution}
          onSelectPlan={selectPlan}
          onOpenPortal={openBillingPortal}
        />
      ) : null}
      {canManageTenantBilling && (!canManagePlatformBilling || tenantBillingView) && !plans.length ? <p className="helper">Plan catalog is empty or unavailable.</p> : null}

      {canManageTenantBilling && (!canManagePlatformBilling || tenantBillingView) ? <BillingCommercialStackPanel current={current} billingCalculation={billingCalculation} /> : null}

      {canManageTenantBilling && (!canManagePlatformBilling || tenantBillingView) ? <BillingInvoicesPanel invoicePreview={invoicePreview} invoices={invoices} /> : null}

      {canEditPlatformBilling ? <p><a href="/admin?workspace=platform-pricing">Pricing &amp; Products</a></p> : null}

  </section>
}
